import { counselSchema } from "@/features/case-evaluation/types/counsel";
import { caseCategorySchema } from "@/features/dashboard/types/case-category";
import { caseTypeSchema } from "@/features/dashboard/types/case-type";
import { courtTypeSchema } from "@/features/dashboard/types/court-type";
import { courtTypeStationSchema } from "@/features/dashboard/types/court-type-station";
import { mediaSchema } from "@/features/dashboard/types/media.ts";
import { provinceSchema } from "@/features/dashboard/types/province";
import { regionSchema } from "@/features/dashboard/types/region";
import { userSchema } from "@/features/dashboard/types/user";
import { z } from "zod";

const caseStageChartSchema = z.object({ data: z.any().optional() });

const caseActivityShareSchema = z.object({
	id: z.string().nullable(),
	share_with: z.array(z.string()),
});

const caseDefendantSchema = z.object({
	id: z.string().nullable(),
	fullname: z.string(),
	email: z.string().email(),
	address: z.string(),
});

const caseRecordSchema = z.object({
	id: z.string(),
	activity_id: z.string().nullable(),
	docket_number: z.string().optional().nullable(),
	case_number: z.string().optional().nullable(),
	service_type: z.string(),
	court_level_id: z.string(),
	court_type_id: z.string(),
	court_type_schema: courtTypeSchema.optional(),
	court_type_station_schema: courtTypeStationSchema.optional(),
	region_code: z.string(),
	province_code: z.string(),
	case_title: z.string(),
	court_station_id: z.string(),
	party_type: z.string(),
	payor_type: z.string(),
	collection_type: z.string(),
	last_name: z.string(),
	middle_name: z.string().optional().nullable(),
	first_name: z.string(),
	email: z.string(),
	summons_count: z.string(),
	status: z.string(),
	defendant_label: z.string().optional().nullable(),
	plaintiff_label: z.string().optional().nullable(),
	collaborators: z.any().optional().nullable(),
	payment_status: z.string().optional().nullable(),
	defer_remarks: z.string().optional().nullable(),
	deferred_at: z.string().optional().nullable(),
	deferred_by: z.string().optional().nullable(),
	stage: z.string().optional().nullable(),
	filed_at: z.string(),
	station: z.string(),
	case_type: z.string(),
	attachments: z.array(mediaSchema),
	fileAttachmentPayment: z.instanceof(File).optional(),
	payment: z.array(mediaSchema).optional(),
	medias: z.array(mediaSchema).optional(),
	case_type_schema: caseTypeSchema.optional(),
	case_defendants: z.any(),
	decision_at: z.any(),
	allow_post_as: z.any(),
	case_category: z.string(),
	case_category_schema: caseCategorySchema.optional(),
	court_category: z.string(),
	court_level: z.string(),
	region: z.string(),
	region_schema: regionSchema.optional(),
	province_schema: provinceSchema.optional(),
	province: z.string(),
	case_classification: z.string().optional().nullable(),
	created_at: z.string(),
	raffled_at: z.string().optional(),
	received_at: z.string().optional(),
	received_by: z.string().optional(),
	branch: z.string().optional(),
	nature_of_action: z.string().optional(),
	plaintiffs: z.array(
		z.object({
			id: z.string(),
			email: z.string().optional(),
			mobile_number: z.string().optional(),
			corporation: z.string().optional(),
			first_name: z.string(),
			middle_name: z.string().optional(),
			last_name: z.string(),
			region_code: z.string(),
			province_code: z.string(),
			city_code: z.string(),
			barangay: z.string(),
			address_1: z.string(),
			address_2: z.string(),
			zipcode: z.union([z.string(), z.number()]).nullable().optional(),
		}),
	),
	plaintiff_counsels: z.array(counselSchema).optional(),
	defendant_counsels: counselSchema.optional(),
});

export const caseRecordFormSchema = z.object({
	id: z.string().optional(),
	docket_number: z.string().optional().nullable(),
	case_number: z.string().optional().nullable(),
	case_counsels: z.array(userSchema).optional(),
	service_type: z.string(),
	case_title: z.string(),
	court_level_id: z.string(),
	court_type_id: z.string(),
	region_code: z.string(),
	province_code: z.string(),
	court_station_id: z.string(),
	party_type: z.string(),
	stage: z.string().optional().nullable(),
	last_name: z.string(),
	middle_name: z.string().optional().nullable(),
	first_name: z.string(),
	email: z.string(),
	summons_count: z.string(),
	nature_of_action: z.string().optional(),
	payment_status: z.string().optional().nullable(),
	defer_remarks: z.string().optional().nullable(),
	filed_at: z.string(),
});

export const caseRecordStatusFormSchema = z.object({
	id: z.string().optional(),
	defer_remarks: z.string().optional().nullable(),
	status: z.string(),
});

export type CaseStageChartSchema = z.infer<typeof caseStageChartSchema>;
export type CaseRecord = z.infer<typeof caseRecordSchema>;
export type CaseDefendant = z.infer<typeof caseDefendantSchema>;
export type CaseDefendantSchema = z.infer<typeof caseDefendantSchema>;
export type CaseActivityShareSchema = z.infer<typeof caseActivityShareSchema>;
export type CaseRecordForm = z.infer<typeof caseRecordFormSchema>;
export type CaseRecordStatusFormSchema = z.infer<typeof caseRecordStatusFormSchema>;
